// Ports `EpochID` and `CompactionRecord` (SuiteModel/DocumentStore+Epochs.swift). A compacted document is a new epoch:
// `epoch + 1` and a random `lineage` at its root, so two devices compacting one epoch before they sync make two
// histories, never merged as one; a document never compacted has lineage "". The newer epoch wins; of two compactions
// of one epoch, the larger lineage. The record relates the new history to the one it replaced: the old document's final
// heads (`fromHeads`) and the new heads for that state (`toHeads`), old → new heads for pinned and recent states, and the
// old heads whose later edits have since been merged in (`absorbedHeads`).
import * as A from '@automerge/automerge';
import { backendOf, type Backend } from './tree.ts';

export interface EpochID {
  epoch: number;
  lineage: string;
}

export const EPOCH_KEY = 'epoch';
export const LINEAGE_KEY = 'lineage';
export const COMPACTION_KEY = 'compaction';

/** `EpochID <`: epoch, then lineage (lineages are lowercase UUIDs, so code-unit order is the Swift's). */
export function compareEpochs(a: EpochID, b: EpochID): number {
  if (a.epoch !== b.epoch) return a.epoch < b.epoch ? -1 : 1;
  return a.lineage === b.lineage ? 0 : a.lineage < b.lineage ? -1 : 1;
}

export const sameEpoch = (a: EpochID, b: EpochID) => a.epoch === b.epoch && a.lineage === b.lineage;
export const describeEpoch = (e: EpochID) => (e.lineage ? `version ${e.epoch} (${e.lineage.slice(0, 8)})` : `version ${e.epoch}`);

function scalar(b: Backend, obj: string, key: string): [string, unknown] | null {
  return (b.getWithType(obj, key) as [string, unknown] | null) ?? null;
}

function int(b: Backend, obj: string, key: string): number | undefined {
  const v = scalar(b, obj, key);
  return v && (v[0] === 'int' || v[0] === 'uint' || v[0] === 'counter') ? Number(v[1]) : undefined;
}

function str(b: Backend, obj: string, key: string): string | undefined {
  const v = scalar(b, obj, key);
  if (!v) return undefined;
  if (v[0] === 'str') return v[1] as string;
  if (v[0] === 'text') return b.text(v[1] as string);
  return undefined;
}

/** A document's epoch and lineage (0 and "" when never compacted). */
export function epochIDOf(doc: A.Doc<unknown> | Backend): EpochID {
  const b = backendOf(doc);
  return { epoch: int(b, '_root', EPOCH_KEY) ?? 0, lineage: str(b, '_root', LINEAGE_KEY) ?? '' };
}

export interface CompactionRecord {
  from: EpochID;
  fromHeads: string;
  toHeads: string;
  headsMap: Record<string, string>;
  absorbedHeads: string;
}

/** The compaction record at the root, if the document was compacted. */
export function compactionRecordOf(doc: A.Doc<unknown> | Backend): CompactionRecord | null {
  const b = backendOf(doc);
  const v = scalar(b, '_root', COMPACTION_KEY);
  if (!v || v[0] !== 'map') return null;
  const obj = v[1] as string;
  const headsMap: Record<string, string> = {};
  for (const line of (str(b, obj, 'headsMap') ?? '').split('\n')) {
    const parts = line.split(' ').filter(Boolean);
    if (parts.length === 2) headsMap[parts[0]] = parts[1];
  }
  return {
    from: { epoch: int(b, obj, 'fromEpoch') ?? 0, lineage: str(b, obj, 'fromLineage') ?? '' },
    fromHeads: str(b, obj, 'fromHeads') ?? '',
    toHeads: str(b, obj, 'toHeads') ?? '',
    headsMap,
    absorbedHeads: str(b, obj, 'absorbedHeads') ?? '',
  };
}

/** `setCompactionRecord`: a new map at the root holding the record (the heads map as one sorted string). */
export function writeCompactionRecord(raw: Backend, record: CompactionRecord): void {
  const obj = raw.putObject('_root', COMPACTION_KEY, {} as never);
  raw.put(obj, 'fromEpoch', record.from.epoch, 'int');
  raw.put(obj, 'fromLineage', record.from.lineage, 'str');
  raw.put(obj, 'fromHeads', record.fromHeads, 'str');
  raw.put(obj, 'toHeads', record.toHeads, 'str');
  const keys = Object.keys(record.headsMap).sort();
  raw.put(obj, 'headsMap', keys.map((k) => `${k} ${record.headsMap[k]}`).join('\n'), 'str');
  if (record.absorbedHeads) raw.put(obj, 'absorbedHeads', record.absorbedHeads, 'str');
}
